import React, { useMemo } from 'react';
import { injectIntl } from 'react-intl';
import { Formik, Form } from 'formik';
// logic
// components
//
//
import useCarrierUserForm from './useCarrierUserForm';
import TicketFormContainer from 'modules/emergencies/components/ui/containers/ticketFormContainer';
import { CustomSticky } from 'modules/emergencies/components/ui/sticky/CustomSticky';
import { FormButton } from 'modules/emergencies/components/ui/buttons/FormButton';
import styled from 'styled-components';
import { QuoteFormErrorInfoBlock } from 'modules/emergencies/components/quoteForm/blocks/quoteFormErrorInfoBlock';
import PremiumSectionContainer from 'modules/emergencies/components/ticketForm/components/PremiumSectionContainer';
import PremiumSection from 'modules/emergencies/components/ticketForm/components/PremiumSection';
import { SectionContainer } from 'modules/emergencies/components/ticketForm/components/SectionContainer';
import SectionTitle from 'modules/emergencies/components/ticketForm/components/SectionTitle';
import { ButtonContainer, Half, SectionBody } from '@ontruck/fe-ui';
import { SectionLine } from 'modules/emergencies/components/ticketForm/components/SectionLine';
import { FormikPremiumInput } from 'modules/emergencies/components/ui/inputs/customInput';
import { ModalRegularText } from 'modules/emergencies/components/modals/modalComponents';
import CheckBoxLabel from 'modules/emergencies/components/ticketForm/components/CheckBoxLabel';
import BasicLoader from 'modules/emergencies/components/ui/loaders/basicLoader';
import useSWR from 'swr';
import getCarrierUserGroups from 'modules/emergencies/api/getCarrierUserGroups';

const LoaderContainer = styled.div`
  text-align: center;
  padding: 32px;
  width: 100%;
`;

const CollapsibleContainer = styled.div`
  padding: 0;
  gap: 4px;
  width: 100%;
`;

const CollapsibleItem = styled.div`
  border-top: 1px solid var(--grey-500);
  padding-left: 0;
  padding-right: 0;
  &:last-of-type {
    border-bottom: 1px solid var(--grey-500);
  }
`;

const CollapsibleHeader = styled.div`
  padding: 12px 8px 12px 8px;
  padding-left: 0;
  padding-right: 0;
  gap: 4px;
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const CollapsibleLabel = styled.div`
  font-family: Roboto;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  text-align: left;
  display: inline-block;
  color: var(--accent-color-blue);
`;

const availableCommunicationLanguages = [
  {
    value: 'en-GB',
    label: 'English',
  },
  {
    value: 'fr-FR',
    label: 'French',
  },
  {
    value: 'it-IT',
    label: 'Italian',
  },
  {
    value: 'pl-PL',
    label: 'Polish',
  },
  {
    value: 'ro-RO',
    label: 'Romanian',
  },
  {
    value: 'es-ES',
    label: 'Spanish',
  },
  {
    value: 'pt',
    label: 'Portuguese',
  },
];

const CarrierUserForm = ({ carrierUserFormData, className }) => {
  const { onSubmit, validationSchema, initialValues, carrierId, isEdit } = carrierUserFormData;

  const { showErrors, setShowErrors, isWaitingForResponse, backendErrors, beforeOnSubmit } =
    useCarrierUserForm({ onSubmit, carrierId });

  const { data: segments, error: segmentsError } = useSWR(`segments/`, getCarrierUserGroups, {
    errorRetryCount: 0,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const isLoadingSegments = typeof segments === 'undefined' && !segmentsError;

  const segmentsByCategory = useMemo(() => {
    if (isLoadingSegments || typeof segments === 'undefined') return {};
    const groupedSegments = segments.results
      .filter((r) => r.parent !== null)
      .reduce((groups, segment) => {
        const name = segment.name;
        const category = segment?.parent?.name || 'Other';

        if (!groups[category]) {
          groups[category] = [];
        }

        //

        groups[category].push({ ...segment, name: name });

        return groups;
      }, {});
    return groupedSegments;
  }, [isLoadingSegments, segments]);

  const segmentsByCategoryKeys = useMemo(() => {
    return Object.keys(segmentsByCategory);
  }, [segmentsByCategory]);

  return (
    <TicketFormContainer className={className}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={beforeOnSubmit()}
      >
        {({ errors, dirty, values, submitForm: submitFormikForm, setFieldValue }) => {
          const carrier_user_groups = values.carrier_user_groups;
          const hasBackendErrors = Object.keys(backendErrors).length > 0;
          let backendErrorsMsg = 'There was an error creating the carrier user';
          if (!!backendErrors.username) backendErrorsMsg = 'There email is already in use';
          if (backendErrors.details === 'can_not_reach_backend')
            backendErrorsMsg = 'There was an error connecting to the server';
          return (
            <Form>
              {/* body */}
              <PremiumSectionContainer>
                <PremiumSection>
                  <SectionContainer>
                    <SectionTitle>User</SectionTitle>
                    <SectionBody>
                      <SectionLine>
                        <FormikPremiumInput
                          type="text"
                          name="first_name"
                          label="Full name"
                          showErrors={showErrors}
                          isRequired={true}
                        />
                        <FormikPremiumInput
                          type="phone"
                          name="phone"
                          label="Phone number"
                          showErrors={showErrors}
                          isRequired={true}
                        />
                      </SectionLine>
                      <SectionLine>
                        <FormikPremiumInput
                          type="text"
                          name="email"
                          label={'E-mail'}
                          showErrors={showErrors}
                          isRequired={true}
                        />
                        <FormikPremiumInput
                          type={'select'}
                          name="language"
                          label="Comm. language"
                          showErrors={showErrors}
                          isRequired={true}
                          availableOptions={availableCommunicationLanguages}
                        />
                      </SectionLine>
                    </SectionBody>
                  </SectionContainer>
                  <SectionContainer>
                    <SectionTitle>Groups</SectionTitle>
                    <SectionBody>
                      <ModalRegularText>
                        Select the groups to which this user belongs
                      </ModalRegularText>
                      {isLoadingSegments && (
                        <LoaderContainer>
                          <BasicLoader size={10} />
                        </LoaderContainer>
                      )}
                      {!isLoadingSegments && (
                        <CollapsibleContainer>
                          {segmentsByCategoryKeys.map((category) => (
                            <CollapsibleItem key={category}>
                              <CollapsibleHeader>
                                <CollapsibleLabel>{category}</CollapsibleLabel>
                              </CollapsibleHeader>
                              {
                                <div
                                  style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    width: '100%',
                                    marginBottom: '16px',
                                  }}
                                >
                                  {segmentsByCategory[category].map((s) => (
                                    <div style={{ width: '50%' }} key={s.id}>
                                      <CheckBoxLabel
                                        backgroundVariant={'grey200'}
                                        style={{ margin: '2px' }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={carrier_user_groups.includes(s.id)}
                                          onChange={() => {
                                            const set = new Set(carrier_user_groups);
                                            if (set.has(s.id)) {
                                              set.delete(s.id);
                                            } else {
                                              set.add(s.id);
                                            }
                                            const newSegmentIds = Array.from(set);
                                            setFieldValue('carrier_user_groups', newSegmentIds);
                                          }}
                                        />
                                        {s.name}
                                      </CheckBoxLabel>
                                    </div>
                                  ))}
                                  {segments.length === 0 && (
                                    <>
                                      <ModalRegularText>No segments available</ModalRegularText>
                                    </>
                                  )}
                                </div>
                              }
                            </CollapsibleItem>
                          ))}
                        </CollapsibleContainer>
                      )}
                    </SectionBody>
                  </SectionContainer>
                </PremiumSection>
              </PremiumSectionContainer>
              <PremiumSectionContainer>
                {/* segments */}
                <PremiumSection>
                  <SectionContainer></SectionContainer>
                </PremiumSection>
              </PremiumSectionContainer>
              {hasBackendErrors && <QuoteFormErrorInfoBlock backendErrorsMsg={backendErrorsMsg} />}
              {/* footer */}
              <CustomSticky>
                <ButtonContainer>
                  <Half>
                    <FormButton
                      isDisabled={isWaitingForResponse}
                      onClick={() => {
                        setShowErrors(true);
                        submitFormikForm();
                      }}
                    >
                      {isEdit ? 'Save changes' : 'Add user'} {hasBackendErrors && <> ⚠️ </>}
                    </FormButton>
                  </Half>
                </ButtonContainer>
              </CustomSticky>
            </Form>
          );
        }}
      </Formik>
    </TicketFormContainer>
  );
};

export default injectIntl(CarrierUserForm);
